<script setup lang="ts">
  import { useRestaurant } from '@/composition/use-restaurant';
  import { useUi } from '@/composition/use-ui';
  import { useModal } from '@/composition/use-modal';
  import { AlertType } from '@/interfaces/IAlert';
  import { IPayment } from '@/interfaces/IPayment';
  import { IOrder } from '@/interfaces/IOrder';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { displayPrice } from '@/utils';
  import { ref, computed, inject, Ref, onMounted, watch } from 'vue';
  import PaymentsService from '@/api/services/payments.service';
  import { PaymentEntityType } from '@/enums/PaymentEntityType';
  import { PaymentStatus, PaymentType } from '@/enums/PaymentEnums';
  import ModalPay from '../../pay/ModalPay.vue';
  import { useAuth } from '@/composition/use-auth';
  import { PermissionType } from '@/enums/PermissionType';
  import ModalAdminPin from '../../ModalAdminPin.vue';
  import { IAuthData } from '@/interfaces/IAuthData';
  import { useOrders } from '@/composition/use-orders';
  import { PAYMENT_METHODS_MAP } from '@/const/payment.consts';
  import { PaymentMethod } from '@/enums/PaymentMethod';

  const props = withDefaults(
    defineProps<{
      showHeader?: boolean;
      showSummary?: boolean;
    }>(),
    {
      showHeader: true,
      showSummary: true,
    }
  );

  const order = inject<Ref<IOrder>>('order')!;

  // POS V2 preview modals re-host this tree read-only: hide the mutating
  // actions (add tip / reimburse). Interactive contexts are unaffected.
  const posV2ViewMode = inject<'interactive' | 'preview'>(
    'posV2ViewMode',
    'interactive'
  );
  const isPreview = posV2ViewMode === 'preview';

  const restaurantStore = useRestaurantStore();
  const ui = useUi();
  const modals = useModal();
  const auth = useAuth();
  const ordersHelper = useOrders();

  const tipPayments = ref<IPayment[]>([]);
  const isLoading = ref(false);
  const showingDetails = ref(false);

  const tipTotal = computed(() => {
    return order.value?.tipTotal || 0;
  });

  const canAddTip = computed(() => {
    const o = order.value;
    return (
      o &&
      o.paymentStatus === 'paid' &&
      o.status !== 'canceled' &&
      o.status !== 'declined'
    );
  });

  async function loadTipPayments() {
    if (!order.value) return;

    try {
      isLoading.value = true;
      const result = await PaymentsService.v2FindByReference(
        PaymentEntityType.Order,
        order.value.id,
        { itemsPerPage: 100 }
      );
      tipPayments.value = result.items.filter(
        (p) => p.type === PaymentType.Tip && p.status === PaymentStatus.Paid
      );
    } catch (e) {
      console.error(e);
    } finally {
      isLoading.value = false;
    }
  }

  async function addTip() {
    modals.show(
      ModalPay as any,
      {
        type: PaymentType.Tip,
        unpaidAmount: 0, // Tips don't have a required amount
        entityIds: [order.value.id],
        title: 'Agregar propina',
        hiddenMethods: [
          PaymentMethod.CustomerAccount,
          PaymentMethod.Employee,
          PaymentMethod.Invitation,
          PaymentMethod.Club,
        ],
      },
      {
        name: 'tip-pay',
        adaptive: true,
        height: 'auto',
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
        clickToClose: false,
      },
      {
        paid: async () => {
          const wasShowingDetails = showingDetails.value;
          await ordersHelper.refreshOrder({ order: order.value });
          await loadTipPayments();
          // Keep details expanded if they were already showing
          if (wasShowingDetails) {
            showingDetails.value = true;
          }
          ui.showToast(AlertType.Success, 'Propina agregada correctamente');
        },
      }
    );
  }

  async function reimburseTip(payment: IPayment) {
    try {
      let authData: IAuthData | undefined;

      if (!auth.hasPermission(PermissionType.OrdersCancel)) {
        authData = await modals.showDynamicDialog<IAuthData>(
          ModalAdminPin,
          {
            requiredPermission: PermissionType.OrdersCancel,
            reason: 'tip-reimburse',
          },
          'admin-pin'
        );

        if (!authData) return;
      }

      const confirmed = await modals.confirm({
        title: 'Reembolsar propina',
        message: `¿Está seguro que desea reembolsar la propina de ${
          restaurantStore.currency.symbol
        }${displayPrice(payment.total)}?`,
      });

      if (!confirmed) return;

      isLoading.value = true;
      await PaymentsService.v2SetStatus(
        payment.id,
        PaymentStatus.Reimbursed,
        authData
      );
      ui.showToast(AlertType.Success, 'Propina reembolsada correctamente');
      await ordersHelper.refreshOrder({ order: order.value });
      await loadTipPayments();
    } catch (e) {
      ui.serverError(e, 'Ocurrió un error al reembolsar la propina');
    } finally {
      isLoading.value = false;
    }
  }

  async function toggleDetails() {
    showingDetails.value = !showingDetails.value;
  }

  // Watch for changes in tipTotal to reload payments only if details are showing
  watch(
    [tipTotal, showingDetails],
    ([newTipTotal, newShowingDetails]) => {
      if (newTipTotal > 0 && newShowingDetails) {
        loadTipPayments();
      }
    },
    { immediate: true }
  );

  // Load payments immediately if we're not showing summary (inline mode)
  onMounted(() => {
    if (!props.showSummary && tipTotal.value > 0) {
      loadTipPayments();
    }
  });
</script>

<template>
  <div
    v-if="tipTotal > 0 || canAddTip"
    class="tips-container"
    :class="{ 'inline-mode': !showSummary }"
  >
    <div v-if="showHeader" class="tips-header">
      <h4 class="tips-title">Propinas</h4>
    </div>

    <div class="tips-body">
      <div v-if="showSummary" class="tip-summary">
        <span class="tip-label">Propina recibida</span>
        <div class="tip-amount-container">
          <span class="tip-amount">
            {{ restaurantStore.currency.symbol }}{{ displayPrice(tipTotal) }}
          </span>
          <v-btn
            v-if="tipTotal > 0"
            icon
            x-small
            @click="toggleDetails"
            :loading="isLoading"
          >
            <v-icon small>
              {{ showingDetails ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
            </v-icon>
          </v-btn>
        </div>
      </div>

      <!-- Tip payments details -->
      <div
        v-if="(showingDetails || !showSummary) && tipPayments.length > 0"
        class="tip-payments"
      >
        <div
          v-for="(payment, index) in tipPayments"
          :key="payment.id"
          class="tip-payment"
        >
          <span class="payment-index">{{ index + 1 }}.</span>
          <span class="payment-info">
            <span class="payment-amount">
              {{ restaurantStore.currency.symbol
              }}{{ displayPrice(payment.total) }}
              <span class="payment-method">
                ({{
                  PAYMENT_METHODS_MAP.get(payment.paymentMethod)?.title ||
                  payment.paymentMethod
                }})
              </span>
            </span>
            <timeago
              class="payment-time"
              :datetime="payment.createdAt"
            ></timeago>
          </span>
          <v-btn
            v-if="!isPreview"
            icon
            x-small
            color="error"
            @click="reimburseTip(payment)"
            :loading="isLoading"
            title="Reembolsar propina"
          >
            <v-icon small>mdi-cash-refund</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- Add tip button -->
      <v-btn
        v-if="canAddTip && !isPreview"
        color="success"
        small
        depressed
        outlined
        block
        @click="addTip"
        :loading="isLoading"
        class="add-tip-btn"
      >
        <v-icon left small>mdi-cash-plus</v-icon>
        Agregar propina
      </v-btn>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .tips-container {
    @apply tw-bg-orange-50 tw-rounded-lg tw-p-3 tw-space-y-2;

    &.inline-mode {
      @apply tw-bg-transparent tw-p-0;
    }

    .tips-header {
      .tips-title {
        @apply tw-font-bold tw-text-sm tw-text-gray-700;
      }
    }

    .tips-body {
      @apply tw-space-y-2;

      .tip-summary {
        @apply tw-flex tw-justify-between tw-items-center;

        .tip-label {
          @apply tw-font-medium tw-text-sm tw-text-gray-600;
        }

        .tip-amount-container {
          @apply tw-flex tw-items-center tw-space-x-1;

          .tip-amount {
            @apply tw-font-bold tw-text-base;
          }
        }
      }

      .tip-payments {
        @apply tw-space-y-1 tw-pl-4 tw-border-l-2 tw-border-dashed tw-border-gray-300;

        .tip-payment {
          @apply tw-flex tw-items-center tw-space-x-2 tw-text-sm;
          font-family: 'Courier New', monospace;

          .payment-index {
            @apply tw-text-gray-500 tw-text-xs tw-font-mono;
          }

          .payment-info {
            @apply tw-flex-1 tw-flex tw-flex-col;

            .payment-amount {
              @apply tw-font-semibold tw-font-mono;

              .payment-method {
                @apply tw-text-xs tw-text-gray-600 tw-font-normal tw-uppercase tw-tracking-wider;
              }
            }

            .payment-time {
              @apply tw-text-xs tw-text-gray-500 tw-font-mono;
            }
          }

          .v-btn {
            // Remove hover-only visibility - buttons always visible
          }
        }
      }

      .add-tip-btn {
        @apply tw-mt-2;
      }
    }
  }
</style>
